import {
    useContext,
    useEffect,
    useState,
} from "react";
import { useSearchParams } from 'react-router-dom';

import { Trans } from 'react-i18next';

import JSZip from "jszip";

import {
    Alert,
    Box,
  Button,
    Typography,
} from '@mui/material';
import {
    DataGrid
} from '@mui/x-data-grid';
import {
    DatePicker,
    LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
    ArrowRightAltOutlined,
    ArrowDownwardOutlined,
} from '@mui/icons-material';

import { de } from 'date-fns/locale/de';

import api from "../../api";
import DateFormat from "../../utils/DateFormat";
import generatePDFfromRecords from "../../utils/PDFGenerator";
import SearchBar from "../shared/SearchBar";
import ConfirmGenerateDialog from "./ConfirmGenerateDialog";
import { Context } from "../../context/context";

function WorkRecordsPage() {
    const [{user}, ] = useContext(Context)
    const [urlParam, setUrlParam] = useSearchParams({ 'search': '' });
    const [allRecords, setAllRecords] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [filter, setFilter] = useState(urlParam.get('search') || '');
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [showError, setShowError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        const readData = async () => {
            let users = await api.readUsers();
            let userIds = users.map(u => u.id);
            let records = await api.readAllWorkRecords(userIds, startDate, endDate);

            // TO DO: Remove filter once backend api is adjusted
            let filteredRecords = records.filter(r => {
                return r.workDays.filter(wd => wd.workEntries.length > 0).length > 0;
            });

            setAllRecords(filteredRecords.map(r => {
                return {
                    ...r,
                    workDays: r.workDays.sort((a, b) => a.date - b.date)
                }
            }));
        };

        readData();
    }, [startDate, endDate]);

    useEffect(() => {
        let displayed = allRecords.filter(r => {
            return r.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) || rowSelectionModel.includes(r.id);
        });
        setFilteredRecords(displayed);
    }, [allRecords, filter]);

    const handleOnFilter = (e) => {
        setFilter(e.target.value);
        setUrlParam({ 'search': e.target.value });
    };

    const handleOnClear = () => {
        setFilter('');
        setUrlParam('');
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const handleOpenDialog = () => {
        if (rowSelectionModel.length === 0) {
            setShowError(true);
            setTimeout(() => { setShowError(false) }, 5000);
            return;
        };
        setDialogOpen(true);
    };

    const determineStartTime = () => {
        if (startDate != null) {
            let start = new Date(startDate);

            return start;
        };

        let min = new Date();

        for (const id of rowSelectionModel) {
            let record = allRecords.find(r => r.id === id);
            if (record?.workDays.length > 0) {
                let d = new Date(record.workDays[0].start);

                if (d.getTime() < min.getTime()) {
                    min = d;
                };
            };
        };
        return min;
    };

    const determineEndTime = () => {
        if (endDate != null) {
            let end = new Date(endDate);

            return end;
        };

        let max = new Date(0);

        for (const id of rowSelectionModel) {
            let record = allRecords.find(r => r.id === id);

            if (record?.workDays.length > 0) {
                let d = record.workDays[record.workDays.length - 1].end;
                
                if (d.getTime() > max.getTime()) {
                    max = d;
                };
            };
        };

        return max;
    }

    const handleGenerate = (withDetails) => {
        let selectedRecords = allRecords.filter(r => rowSelectionModel.includes(r.id));

        let start = determineStartTime();
        let end = determineEndTime();
        let zip = new JSZip();
        let folderName = `Arbeitsnachweise  ${DateFormat.formatDDMMYY(start)} - ${DateFormat.formatDDMMYY(end)}`;
        let folder = zip.folder(folderName);

        generatePDFfromRecords(selectedRecords, withDetails, start, end, user.name).then(res => {
            for (let i = 0; i < selectedRecords.length; i++) {
                folder.file(
                    `${selectedRecords[i].name} ${DateFormat.formatDDMMYY(start)} - ${DateFormat.formatDDMMYY(end)}.pdf`,
                    res[i],
                );
            };

            folder.generateAsync({ type: 'blob' }).then(blob => {
                const element = document.createElement("a");
                element.download = `${folderName}.zip`;
                element.href = URL.createObjectURL(blob);
                document.body.appendChild(element);
                element.click();
            });
        });
    };

    const columns = [{ field: 'name', headerName: 'Name', editable: false, flex: 1 }];
    const rows = filteredRecords.map(r => ({ id: r.id, name: r.name }));

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                <Typography variant='overline' gutterBottom><Trans i18nKey='filter' ns='workRecordsPage'>Filter</Trans></Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px', mb: 5 }}>
                    <DatePicker label={<Trans i18nKey='von' ns='workRecordsPage'>Von</Trans>} value={startDate} onChange={(val) => setStartDate(val)} />
                    <ArrowRightAltOutlined />
                    <DatePicker label={<Trans i18nKey='bis' ns='workRecordsPage'>Bis</Trans>} value={endDate} onChange={(val) => setEndDate(val)} />
                    <Box sx={{ display: 'flex', width: 'fit-content', ml: 'auto' }}>
                        {
                            showError &&
                            <Alert severity='error' sx={{ width: 'fit-content', mr: 5 }}><Trans i18nKey='keineNutzerError' ns='workRecordsPage'>Keine Nutzer ausgewählt</Trans></Alert>
                        }
                        <Button onClick={handleOpenDialog} variant="contained">
                            <ArrowDownwardOutlined />
                            <Trans i18nKey='nachweiseGenerieren' ns='workRecordsPage'>Nachweise generieren</Trans>
                        </Button>
                        <ConfirmGenerateDialog
                            open={dialogOpen}
                            onClose={handleCloseDialog}
                            onSubmit={handleGenerate}
                            sumUser={rowSelectionModel.length}
                            start={DateFormat.formatDDMMYY(determineStartTime())}
                            end={DateFormat.formatDDMMYY(determineEndTime())}
                        />
                    </Box>
                </Box>
                <SearchBar
                    value={filter}
                    onChange={handleOnFilter}
                    onClear={handleOnClear}
                />
                <DataGrid
                    sx={{ mt: 5 }}
                    rows={rows}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 5,
                            },
                        },
                    }}
                    pageSizeOptions={[5]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(newRowSelectionModel) => {
                        setRowSelectionModel(newRowSelectionModel);
                    }}
                    rowSelectionModel={rowSelectionModel}
                />
            </LocalizationProvider>
        </>
    );
};

export default WorkRecordsPage
